'use client';

import React, { useState, useRef, useCallback } from 'react';
import type { OutfitData, WizardData } from '../lib/types';
import { trackOutfitShared } from '@/lib/analytics';

interface OutfitResultProps {
  outfit: OutfitData;
  wizardData: WizardData;
  onReset: () => void;
}

// ─── Canvas card generator ────────────────────────────────────────────────────
function generateOutfitCard(outfit: OutfitData, wizardData: WizardData): HTMLCanvasElement {
  const W = 1200;
  const H = 630;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;

  const bg = ctx.createLinearGradient(0, 0, W, H);
  bg.addColorStop(0, '#1A1A1A');
  bg.addColorStop(0.5, '#2D2520');
  bg.addColorStop(1, '#3A2E25');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  const glow1 = ctx.createRadialGradient(W * 0.15, H * 0.5, 0, W * 0.15, H * 0.5, W * 0.45);
  glow1.addColorStop(0, 'rgba(201,169,110,0.18)');
  glow1.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = glow1;
  ctx.fillRect(0, 0, W, H);

  const glow2 = ctx.createRadialGradient(W * 0.85, H * 0.15, 0, W * 0.85, H * 0.15, W * 0.4);
  glow2.addColorStop(0, 'rgba(201,169,110,0.12)');
  glow2.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = glow2;
  ctx.fillRect(0, 0, W, H);

  const topBar = ctx.createLinearGradient(0, 0, W, 0);
  topBar.addColorStop(0, '#C9A96E');
  topBar.addColorStop(0.5, '#E8C98A');
  topBar.addColorStop(1, '#C9A96E');
  ctx.fillStyle = topBar;
  ctx.fillRect(0, 0, W, 4);

  const botBar = ctx.createLinearGradient(0, 0, W, 0);
  botBar.addColorStop(0, 'rgba(0,0,0,0)');
  botBar.addColorStop(0.3, '#C9A96E');
  botBar.addColorStop(0.6, '#E8C98A');
  botBar.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = botBar;
  ctx.fillRect(0, H - 3, W, 3);

  const PAD = 64;
  ctx.font = 'bold 22px serif';
  ctx.fillStyle = '#C9A96E';
  ctx.fillText('Atelier.ai', PAD, 72);

  const badgeText = '✦ Shared Look';
  ctx.font = '600 12px sans-serif';
  const badgeW = ctx.measureText(badgeText).width + 32;
  const badgeX = W - PAD - badgeW;
  ctx.strokeStyle = 'rgba(201,169,110,0.35)';
  ctx.lineWidth = 1;
  roundRect(ctx, badgeX, 54, badgeW, 28, 14);
  ctx.stroke();
  ctx.fillStyle = 'rgba(201,169,110,0.15)';
  roundRect(ctx, badgeX, 54, badgeW, 28, 14);
  ctx.fill();
  ctx.fillStyle = '#C9A96E';
  ctx.fillText(badgeText, badgeX + 16, 72);

  const titleFontSize = outfit.editorialTitle.length > 40 ? 44 : 56;
  ctx.font = `bold ${titleFontSize}px serif`;
  ctx.fillStyle = '#FFFFFF';
  const titleLines = wrapText(ctx, outfit.editorialTitle, W - PAD * 2 - 200);

  // Draw title lines
  titleLines.slice(0, 2).forEach((line, i) => {
    ctx.fillText(line, PAD, 160 + i * (titleFontSize + 12));
  });

  // Subtitle
  if (outfit.editorialSubtitle) {
    ctx.font = '18px sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.65)';
    ctx.fillText(outfit.editorialSubtitle, PAD, 160 + titleLines.slice(0, 2).length * (titleFontSize + 12) + 28);
  }

  // Items list
  const itemsY = 320;
  ctx.font = '600 13px sans-serif';
  ctx.fillStyle = '#C9A96E';
  ctx.fillText('OUTFIT DETAILS', PAD, itemsY);
  outfit.items.slice(0, 5).forEach((item, i) => {
    ctx.font = '14px sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    ctx.fillText(`• ${item.name} — ${item.estimatedPrice}`, PAD, itemsY + 28 + i * 26);
  });

  // Occasion + budget
  ctx.font = '13px sans-serif';
  ctx.fillStyle = 'rgba(201,169,110,0.75)';
  ctx.fillText(`${wizardData.occasion} · ${wizardData.currency}${wizardData.budget}`, PAD, H - 48);

  return canvas;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';
  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (ctx.measureText(testLine).width > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }
  if (currentLine) lines.push(currentLine);
  return lines;
}

// ─── Caption builder ──────────────────────────────────────────────────────────
function buildCaption(outfit: OutfitData, wizardData: WizardData, platform: 'pinterest' | 'instagram' | 'tiktok'): string {
  const itemNames = outfit.items.slice(0, 4).map((i) => i.name).join(', ');
  const hashtags = [
    '#AtelierAI',
    '#OutfitInspo',
    `#${outfit.aesthetic?.replace(/\s+/g, '') ?? 'StyleAI'}`,
    `#${wizardData.occasion?.replace(/\s+/g, '') ?? 'Fashion'}`,
    '#OOTD',
    '#FashionAI',
  ].join(' ');

  if (platform === 'pinterest') {
    return `${outfit.editorialTitle} — ${outfit.editorialSubtitle ?? ''}\n\nStylist notes: ${outfit.stylistNotes ?? ''}\n\nKey pieces: ${itemNames}\n\nDiscover your AI-curated look at atelier.ai\n\n${hashtags}`;
  }
  if (platform === 'instagram') {
    return `${outfit.editorialTitle} ✨\n\n${outfit.stylistNotes ?? ''}\n\nKey pieces: ${itemNames}\n\nCurated by @atelier.ai for ${wizardData.occasion} — budget ${wizardData.currency}${wizardData.budget}\n\n${hashtags}`;
  }
  // tiktok
  return `${outfit.editorialTitle} 🎬 AI-styled look for ${wizardData.occasion} | ${itemNames} | atelier.ai ${hashtags} #FashionTok #StyleTok`;
}

// ─── Share modal ──────────────────────────────────────────────────────────────
interface ShareModalProps {
  platform: 'pinterest' | 'instagram' | 'tiktok';
  caption: string;
  imageDataUrl: string | null;
  onClose: () => void;
}

function ShareModal({ platform, caption, imageDataUrl, onClose }: ShareModalProps) {
  const [copied, setCopied] = useState(false);
  const [imgCopied, setImgCopied] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const platformMeta = {
    pinterest: {
      label: 'Pinterest',
      color: '#E60023',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
          <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" />
        </svg>
      ),
      url: (cap: string) =>
        `https://pinterest.com/pin/create/button/?description=${encodeURIComponent(cap)}&url=${encodeURIComponent('https://atelier.ai')}`,
      cta: 'Open Pinterest',
      instruction: 'Copy the caption below, then click "Open Pinterest" to create your Pin. Upload the image card when prompted.',
    },
    instagram: {
      label: 'Instagram',
      color: '#E1306C',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
        </svg>
      ),
      url: () => `https://www.instagram.com/`,
      cta: 'Open Instagram',
      instruction: 'Download the image card, copy the caption, then open Instagram to create a new post.',
    },
    tiktok: {
      label: 'TikTok',
      color: '#010101',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.76a4.85 4.85 0 0 1-1.01-.07z" />
        </svg>
      ),
      url: () => `https://www.tiktok.com/upload`,
      cta: 'Open TikTok',
      instruction: 'Download the image card, copy the caption, then open TikTok to create a new post or story.',
    },
  };

  const meta = platformMeta[platform];

  const handleCopyCaption = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // fallback
      const ta = document.createElement('textarea');
      ta.value = caption;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const handleDownloadImage = () => {
    if (!imageDataUrl) return;
    const a = document.createElement('a');
    a.href = imageDataUrl;
    a.download = `atelier-outfit-${platform}.png`;
    a.click();
  };

  const handleOpenPlatform = () => {
    window.open(meta.url(caption), '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div
        className="relative w-full max-w-md bg-[#1A1A1A] border border-[#C9A96E]/30 rounded-2xl p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2.5">
            <span style={{ color: meta.color }}>{meta.icon}</span>
            <span className="text-white font-semibold text-base">Share to {meta.label}</span>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white/80 transition-colors">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Instruction */}
        <p className="text-white/55 text-xs mb-4 leading-relaxed">{meta.instruction}</p>

        {/* Image preview + download */}
        {imageDataUrl && (
          <div className="mb-4">
            <img
              src={imageDataUrl}
              alt="Outfit card preview"
              className="w-full rounded-lg border border-[#C9A96E]/20 object-cover"
              style={{ maxHeight: 180 }}
            />
            <button
              onClick={handleDownloadImage}
              className="mt-2 w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-[#C9A96E]/30 text-[#C9A96E] text-xs font-medium hover:bg-[#C9A96E]/10 transition-colors"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
              </svg>
              Download Image Card
            </button>
          </div>
        )}

        {/* Caption */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-white/50 text-xs uppercase tracking-wider">Pre-filled Caption</span>
            <button
              onClick={handleCopyCaption}
              className="text-xs text-[#C9A96E] hover:text-[#E8C98A] transition-colors font-medium"
            >
              {copied ? '✓ Copied!' : 'Copy'}
            </button>
          </div>
          <textarea
            readOnly
            value={caption}
            rows={5}
            className="w-full bg-[#111] border border-[#C9A96E]/20 rounded-lg px-3 py-2.5 text-white/75 text-xs resize-none focus:outline-none focus:border-[#C9A96E]/50 leading-relaxed"
          />
        </div>

        {/* Upload your own image */}
        <div className="mb-5">
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={() => {}} />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border border-dashed border-white/20 text-white/40 text-xs hover:border-[#C9A96E]/40 hover:text-[#C9A96E]/70 transition-colors"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-4 h-4">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Upload your own photo instead
          </button>
        </div>

        {/* CTA */}
        <button
          onClick={handleOpenPlatform}
          style={{ backgroundColor: meta.color }}
          className="w-full py-3 rounded-xl text-white font-semibold text-sm flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
        >
          {meta.icon}
          {meta.cta}
        </button>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function OutfitResult({ outfit, wizardData, onReset }: OutfitResultProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [shared, setShared] = useState(false);
  const [activeModal, setActiveModal] = useState<'pinterest' | 'instagram' | 'tiktok' | null>(null);
  const [cardDataUrl, setCardDataUrl] = useState<string | null>(null);

  const getOrGenerateCard = useCallback((): string | null => {
    if (cardDataUrl) return cardDataUrl;
    try {
      const canvas = generateOutfitCard(outfit, wizardData);
      const url = canvas.toDataURL('image/png');
      setCardDataUrl(url);
      return url;
    } catch {
      return null;
    }
  }, [cardDataUrl, outfit, wizardData]);

  const handleShare = () => {
    const canvas = generateOutfitCard(outfit, wizardData);
    canvas.toBlob((blob) => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'outfit.png';
        a.click();
        URL.revokeObjectURL(url);
        setShared(true);
      }
    });
  };

  const openShareModal = (platform: 'pinterest' | 'instagram' | 'tiktok') => {
    getOrGenerateCard();
    setActiveModal(platform);
    trackOutfitShared({
      method: platform,
      occasion: wizardData.occasion,
      aesthetic: outfit.aesthetic,
    });
  };

  const socialButtons: { platform: 'pinterest' | 'instagram' | 'tiktok'; label: string; color: string; hoverColor: string; icon: React.ReactNode }[] = [
    {
      platform: 'pinterest',
      label: 'Pinterest',
      color: '#E60023',
      hoverColor: '#c4001d',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
          <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" />
        </svg>
      ),
    },
    {
      platform: 'instagram',
      label: 'Instagram',
      color: '#E1306C',
      hoverColor: '#c1255a',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
        </svg>
      ),
    },
    {
      platform: 'tiktok',
      label: 'TikTok',
      color: '#010101',
      hoverColor: '#333',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.76a4.85 4.85 0 0 1-1.01-.07z" />
        </svg>
      ),
    },
  ];

  return (
    <>
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      <div className="min-h-screen py-10 px-4" style={{ backgroundColor: '#FAF7F2' }}>
        <div className="max-w-2xl mx-auto space-y-8">

          {/* Editorial Header */}
          <div className="space-y-2">
            {outfit.aesthetic && (
              <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#C9A96E' }}>
                ✦ {outfit.aesthetic}
              </p>
            )}
            <h1 className="text-3xl font-bold leading-tight" style={{ color: '#1A1A1A', fontFamily: 'Georgia, serif' }}>
              {outfit.editorialTitle}
            </h1>
            {outfit.editorialSubtitle && (
              <p className="text-base" style={{ color: '#6B6B6B' }}>{outfit.editorialSubtitle}</p>
            )}
          </div>

          {/* Stylist Notes */}
          {outfit.stylistNotes && (
            <div className="rounded-2xl p-5 border" style={{ backgroundColor: '#F5EFE6', borderColor: '#E8D9C0' }}>
              <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: '#C9A96E' }}>
                Stylist Notes
              </p>
              <p className="text-sm leading-relaxed" style={{ color: '#3D3D3D' }}>{outfit.stylistNotes}</p>
            </div>
          )}

          {/* Outfit Items */}
          {outfit.items && outfit.items.length > 0 && (
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#C9A96E' }}>
                Outfit Details
              </p>
              <div className="space-y-3">
                {outfit.items.map((item, idx) => (
                  <div
                    key={item.id ?? idx}
                    className="flex items-start justify-between rounded-xl p-4 border"
                    style={{ backgroundColor: '#FFFFFF', borderColor: '#E8D9C0' }}
                  >
                    <div className="flex-1 min-w-0 pr-4">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium uppercase tracking-wider" style={{ color: '#C9A96E' }}>
                          {item.category}
                        </span>
                      </div>
                      <p className="text-sm font-semibold" style={{ color: '#1A1A1A' }}>{item.name}</p>
                      {item.stylingNote && (
                        <p className="text-xs mt-1 leading-relaxed" style={{ color: '#6B6B6B' }}>{item.stylingNote}</p>
                      )}
                    </div>
                    <div className="shrink-0 text-right space-y-2">
                      <span className="block text-sm font-semibold" style={{ color: '#1A1A1A' }}>{item.estimatedPrice}</span>
                      {item.searchQuery && (
                        <a
                          href={`https://www.google.com/search?tbm=shop&q=${encodeURIComponent(item.searchQuery)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border transition-colors hover:opacity-80"
                          style={{ borderColor: '#C9A96E', color: '#C9A96E', backgroundColor: '#FAF7F2' }}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3 h-3">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                          </svg>
                          Shop
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Keywords */}
          {outfit.keyWords && outfit.keyWords.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {outfit.keyWords.map((kw, i) => (
                <span
                  key={i}
                  className="px-3 py-1 rounded-full text-xs font-medium border"
                  style={{ backgroundColor: '#F5EFE6', borderColor: '#E8D9C0', color: '#6B6B6B' }}
                >
                  {kw}
                </span>
              ))}
            </div>
          )}

          {/* Divider */}
          <div className="border-t" style={{ borderColor: '#E8D9C0' }} />

          {/* Share to social */}
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#C9A96E' }}>
              Share Your Look
            </p>
            <div className="flex flex-wrap gap-2">
              {socialButtons.map(({ platform, label, color, icon }) => (
                <button
                  key={platform}
                  onClick={() => openShareModal(platform)}
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm font-medium transition-opacity hover:opacity-85 active:scale-95"
                  style={{ backgroundColor: color }}
                >
                  {icon}
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Download Card / Start Over */}
          <div className="flex gap-3">
            <button
              onClick={handleShare}
              className="px-5 py-2.5 rounded-full text-sm font-medium border transition-colors hover:opacity-80"
              style={{ borderColor: '#C9A96E', color: '#C9A96E', backgroundColor: 'transparent' }}
            >
              {shared ? 'Downloaded!' : 'Download Card'}
            </button>
            <button
              onClick={onReset}
              className="px-5 py-2.5 rounded-full text-sm font-medium border transition-colors hover:opacity-80"
              style={{ borderColor: '#D0C8BE', color: '#6B6B6B', backgroundColor: 'transparent' }}
            >
              Start Over
            </button>
          </div>

        </div>
      </div>

      {/* Share modal */}
      {activeModal && (
        <ShareModal
          platform={activeModal}
          caption={buildCaption(outfit, wizardData, activeModal)}
          imageDataUrl={cardDataUrl}
          onClose={() => setActiveModal(null)}
        />
      )}
    </>
  );
}